import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAdmin } from "@/context/AdminContext";
import { useDarkMode } from "@/context/DarkModeContext";
import { useAdminStorage } from "@/hooks/useAdminStorage";
import AdminEditField from "@/components/feature/AdminEditField";
import RichText from "@/components/base/RichText";
import IconPicker from "@/components/feature/IconPicker";
import { defaultFooter, type FooterData, type SocialLink, type FooterNavLink } from "@/data/siteContent";

const ICON_OPTIONS = [
  "ri-instagram-line", "ri-tiktok-line", "ri-mail-line", "ri-facebook-line",
  "ri-twitter-x-line", "ri-youtube-line", "ri-pinterest-line", "ri-linkedin-line",
  "ri-phone-line", "ri-whatsapp-line", "ri-telegram-line", "ri-global-line",
];

export default function Footer() {
  const { isAdmin, getLogoFont, logoConfig, setLogoConfig } = useAdmin();
  const logoFont = getLogoFont();
  const { isDark } = useDarkMode();
  const navigate = useNavigate();
  const [data, setData] = useAdminStorage<FooterData>("ma_footer_v3", defaultFooter);
  const [showLogoEditor, setShowLogoEditor] = useState(false);
  const [logoDraft, setLogoDraft] = useState(logoConfig);
  const [editingSocial, setEditingSocial] = useState<string | null>(null);
  const [socialDraft, setSocialDraft] = useState("");
  const [editingNavId, setEditingNavId] = useState<string | null>(null);
  const [navDraft, setNavDraft] = useState<Partial<FooterNavLink>>({});
  const [addingTo, setAddingTo] = useState<"forArtists" | "spaces" | null>(null);
  const [newNav, setNewNav] = useState<Partial<FooterNavLink>>({ label: "", href: "", external: false });

  const set = useCallback(<K extends keyof FooterData>(key: K, val: FooterData[K]) => {
    setData({ ...data, [key]: val });
  }, [data, setData]);

  const updateSocialHref = useCallback((id: string, href: string) => {
    const socialLinks = data.socialLinks.map((s) => s.id === id ? { ...s, href } : s);
    setData({ ...data, socialLinks });
  }, [data, setData]);

  const toggleSocialVisible = useCallback((id: string) => {
    const socialLinks = data.socialLinks.map((s) => s.id === id ? { ...s, visible: !s.visible } : s);
    setData({ ...data, socialLinks });
  }, [data, setData]);

  const removeNavLink = useCallback((section: "forArtistsLinks" | "spacesLinks", id: string) => {
    setData({ ...data, [section]: (data[section] as FooterNavLink[]).filter((l) => l.id !== id) });
  }, [data, setData]);

  const saveNavEdit = useCallback(() => {
    if (!editingNavId) return;
    const updateSection = (section: "forArtistsLinks" | "spacesLinks") => {
      const updated = (data[section] as FooterNavLink[]).map((l) => l.id === editingNavId ? { ...l, ...navDraft } as FooterNavLink : l);
      setData({ ...data, [section]: updated });
    };
    if (data.forArtistsLinks.find((l) => l.id === editingNavId)) updateSection("forArtistsLinks");
    else if (data.spacesLinks.find((l) => l.id === editingNavId)) updateSection("spacesLinks");
    setEditingNavId(null);
    setNavDraft({});
  }, [data, setData, editingNavId, navDraft]);

  const addNavLink = useCallback(() => {
    if (!newNav.label || !newNav.href || !addingTo) return;
    const id = `nav-${Date.now()}`;
    const section = addingTo === "forArtists" ? "forArtistsLinks" : "spacesLinks";
    setData({ ...data, [section]: [...(data[section] as FooterNavLink[]), { id, label: newNav.label!, href: newNav.href!, external: !!newNav.external }] });
    setNewNav({ label: "", href: "", external: false });
    setAddingTo(null);
  }, [data, setData, addingTo, newNav]);

  const scrollTo = (id: string) => {
    document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const bg = isDark ? "bg-gray-900 border-gray-700" : "border-pink-200";
  const headingColor = isDark ? "text-gray-100" : "text-gray-900";
  const bodyColor = isDark ? "text-gray-400" : "text-gray-500";
  const linkHover = isDark ? "hover:text-pink-400" : "hover:text-pink-500";
  const socialBg = isDark ? "bg-gray-700 hover:bg-pink-500 text-gray-300 hover:text-white" : "bg-pink-200 hover:bg-pink-400 hover:text-white text-pink-600";
  const divider = isDark ? "border-gray-700" : "border-pink-200";
  const smallInputCls = "border border-gray-600 bg-gray-800 text-white rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-pink-400 w-full";

  const saveLogo = () => {
    setLogoConfig(logoDraft);
    setShowLogoEditor(false);
  };

  const visibleSocials = isAdmin ? data.socialLinks : data.socialLinks.filter((s) => s.visible);

  const renderNavLinks = (links: FooterNavLink[], section: "forArtistsLinks" | "spacesLinks") => (
    <ul className="space-y-2">
      {links.map((item) => (
        <li key={item.id} className="flex items-center gap-1 group">
          <a
            href={item.href}
            onClick={(e) => { e.preventDefault(); if (item.external) { navigate(item.href); } else { scrollTo(item.href); } }}
            className={`text-sm cursor-pointer transition-colors flex-1 ${bodyColor} ${linkHover}`}
          >
            {item.label}
          </a>
          {isAdmin && (
            <div className="hidden group-hover:flex items-center gap-0.5">
              <button
                onClick={() => { setEditingNavId(item.id); setNavDraft({ ...item }); }}
                className="w-5 h-5 flex items-center justify-center text-pink-400 hover:text-pink-300 cursor-pointer"
                title="Edit"
              >
                <i className="ri-edit-line" style={{ fontSize: "10px" }}></i>
              </button>
              <button
                onClick={() => removeNavLink(section, item.id)}
                className="w-5 h-5 flex items-center justify-center text-red-400 hover:text-red-300 cursor-pointer"
                title="Remove"
              >
                <i className="ri-delete-bin-line" style={{ fontSize: "10px" }}></i>
              </button>
            </div>
          )}
        </li>
      ))}
      {isAdmin && (
        addingTo === (section === "forArtistsLinks" ? "forArtists" : "spaces") ? (
          <li className="space-y-1.5 pt-1">
            <input type="text" placeholder="Label" value={newNav.label || ""} onChange={(e) => setNewNav({ ...newNav, label: e.target.value })} className={smallInputCls} />
            <input type="text" placeholder="URL or #anchor" value={newNav.href || ""} onChange={(e) => setNewNav({ ...newNav, href: e.target.value })} className={smallInputCls} />
            <label className="flex items-center gap-1 text-xs text-gray-400 cursor-pointer">
              <input type="checkbox" checked={!!newNav.external} onChange={(e) => setNewNav({ ...newNav, external: e.target.checked })} className="accent-pink-500" />
              External page
            </label>
            <div className="flex gap-2">
              <button onClick={addNavLink} className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full cursor-pointer whitespace-nowrap">Add</button>
              <button onClick={() => setAddingTo(null)} className="text-gray-400 text-xs cursor-pointer whitespace-nowrap">Cancel</button>
            </div>
          </li>
        ) : (
          <li>
            <button
              onClick={() => setAddingTo(section === "forArtistsLinks" ? "forArtists" : "spaces")}
              className="text-xs text-green-500 hover:text-green-400 flex items-center gap-1 cursor-pointer mt-1"
            >
              <i className="ri-add-line"></i> Add link
            </button>
          </li>
        )
      )}
    </ul>
  );

  return (
    <footer
      className={`border-t transition-colors duration-300 ${bg}`}
      style={isDark ? {} : { background: "#fce7f3" }}
    >
      <div className={`max-w-7xl mx-auto px-6 pt-14 pb-14 ${isAdmin ? "pb-24" : ""}`}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-12">
          {/* Brand */}
          <div className="md:col-span-1">
            <div className="flex items-center gap-2 mb-4 relative group">
              {logoConfig.mode === "image" && logoConfig.imageUrl ? (
                <img
                  src={logoConfig.imageUrl}
                  alt="Logo"
                  className="h-8 w-auto object-contain"
                />
              ) : (
                <>
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-pink-400">
                    <i className={`${logoConfig.icon} text-white text-sm`}></i>
                  </div>
                  <span
                    className={`font-bold text-xl ${headingColor}`}
                    style={{ fontFamily: logoFont.fontFamily, ...logoFont.style }}
                  >
                    {logoConfig.text}
                  </span>
                </>
              )}
              {isAdmin && (
                <div className="hidden group-hover:flex items-center gap-1 absolute -top-6 left-0 bg-gray-900 rounded-lg px-2 py-1 z-20 whitespace-nowrap">
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowLogoEditor(true); }}
                    className="text-pink-400 hover:text-pink-300 cursor-pointer p-0.5"
                    title="Edit logo"
                  >
                    <i className="ri-edit-line text-xs"></i>
                  </button>
                </div>
              )}
            </div>
            {(isAdmin || data.tagline) && (
              isAdmin ? (
                <AdminEditField value={data.tagline} onSave={(v) => set("tagline", v)} onDelete={() => set("tagline", "")} multiline richText className="mb-4" inputClassName="text-sm">
                  <RichText html={data.tagline} as="p" className={`text-sm leading-relaxed mb-4 ${bodyColor}`} />
                </AdminEditField>
              ) : (
                <RichText html={data.tagline} as="p" className={`text-sm leading-relaxed mb-4 ${bodyColor}`} />
              )
            )}

            {/* Social links */}
            <div className="flex gap-3 flex-wrap">
              {visibleSocials.map((s) => (
                <div key={s.id} className="relative group">
                  {isAdmin ? (
                    <div className="flex flex-col items-center gap-1">
                      <div className="relative">
                        <a
                          href={s.href}
                          aria-label={s.label}
                          className={`w-9 h-9 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
                            s.visible ? socialBg : "bg-gray-200 text-gray-400 opacity-50"
                          }`}
                          onClick={(e) => e.preventDefault()}
                        >
                          <i className={`${s.icon} text-base`}></i>
                        </a>
                        <div className="absolute -top-1 -right-1 flex gap-0.5">
                          <button
                            type="button"
                            onClick={() => toggleSocialVisible(s.id)}
                            title={s.visible ? "Hide button" : "Show button"}
                            className={`w-4 h-4 flex items-center justify-center rounded-full text-white cursor-pointer ${s.visible ? "bg-green-400" : "bg-gray-400"}`}
                          >
                            <i className={`${s.visible ? "ri-eye-line" : "ri-eye-off-line"} text-xs`} style={{ fontSize: "8px" }}></i>
                          </button>
                        </div>
                      </div>
                      {editingSocial === s.id ? (
                        <div className="absolute bottom-full mb-1 left-0 z-20 bg-white border border-pink-200 rounded-xl p-2 w-48">
                          <input
                            type="text"
                            value={socialDraft}
                            onChange={(e) => setSocialDraft(e.target.value)}
                            placeholder="https://..."
                            className="w-full border border-gray-200 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-pink-300"
                            autoFocus
                          />
                          <div className="flex gap-1 mt-1">
                            <button
                              type="button"
                              onClick={() => { updateSocialHref(s.id, socialDraft); setEditingSocial(null); }}
                              className="text-xs bg-green-500 text-white px-2 py-0.5 rounded-full cursor-pointer whitespace-nowrap"
                            >Save</button>
                            <button
                              type="button"
                              onClick={() => setEditingSocial(null)}
                              className="text-xs text-gray-400 cursor-pointer whitespace-nowrap"
                            >Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => { setEditingSocial(s.id); setSocialDraft(s.href); }}
                          className="text-xs text-pink-400 hover:text-pink-600 cursor-pointer whitespace-nowrap"
                        >
                          {s.href === "#" || s.href === "" ? "Set URL" : "Edit URL"}
                        </button>
                      )}
                    </div>
                  ) : (
                    <a
                      href={s.href}
                      aria-label={s.label}
                      className={`w-9 h-9 flex items-center justify-center rounded-full transition-colors cursor-pointer ${socialBg}`}
                    >
                      <i className={`${s.icon} text-base`}></i>
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* For Artists */}
          <div>
            {(isAdmin || data.forArtistsTitle) && (
              isAdmin ? (
                <AdminEditField value={data.forArtistsTitle} onSave={(v) => set("forArtistsTitle", v)} onDelete={() => set("forArtistsTitle", "")} className="mb-4" inputClassName="font-bold text-sm uppercase tracking-widest">
                  <h4 className={`font-bold text-sm uppercase tracking-widest mb-4 ${headingColor}`}>{data.forArtistsTitle}</h4>
                </AdminEditField>
              ) : (
                <h4 className={`font-bold text-sm uppercase tracking-widest mb-4 ${headingColor}`}>{data.forArtistsTitle}</h4>
              )
            )}
            {renderNavLinks(data.forArtistsLinks, "forArtistsLinks")}
          </div>

          {/* Spaces */}
          <div>
            {(isAdmin || data.spacesTitle) && (
              isAdmin ? (
                <AdminEditField value={data.spacesTitle} onSave={(v) => set("spacesTitle", v)} onDelete={() => set("spacesTitle", "")} className="mb-4" inputClassName="font-bold text-sm uppercase tracking-widest">
                  <h4 className={`font-bold text-sm uppercase tracking-widest mb-4 ${headingColor}`}>{data.spacesTitle}</h4>
                </AdminEditField>
              ) : (
                <h4 className={`font-bold text-sm uppercase tracking-widest mb-4 ${headingColor}`}>{data.spacesTitle}</h4>
              )
            )}
            {renderNavLinks(data.spacesLinks, "spacesLinks")}
          </div>

          {/* Visit */}
          <div>
            {(isAdmin || data.visitSectionTitle) && (
              isAdmin ? (
                <AdminEditField value={data.visitSectionTitle} onSave={(v) => set("visitSectionTitle", v)} onDelete={() => set("visitSectionTitle", "")} className="mb-4" inputClassName="font-bold text-sm uppercase tracking-widest">
                  <h4 className={`font-bold text-sm uppercase tracking-widest mb-4 ${headingColor}`}>{data.visitSectionTitle}</h4>
                </AdminEditField>
              ) : (
                <h4 className={`font-bold text-sm uppercase tracking-widest mb-4 ${headingColor}`}>{data.visitSectionTitle}</h4>
              )
            )}
            <ul className="space-y-2 text-sm">
              <li className={`flex items-start gap-2 ${bodyColor}`}>
                <i className="ri-map-pin-line text-pink-400 mt-0.5 flex-shrink-0"></i>
                {isAdmin ? (
                  <AdminEditField value={data.visitAddress} onSave={(v) => set("visitAddress", v)} inputClassName="text-sm">
                    <span>{data.visitAddress}</span>
                  </AdminEditField>
                ) : (
                  <span>{data.visitAddress}</span>
                )}
              </li>
              <li className={`flex items-center gap-2 ${bodyColor}`}>
                <i className="ri-time-line text-pink-400 flex-shrink-0"></i>
                {isAdmin ? (
                  <AdminEditField value={data.visitHours} onSave={(v) => set("visitHours", v)} inputClassName="text-sm">
                    <span>{data.visitHours}</span>
                  </AdminEditField>
                ) : (
                  <span>{data.visitHours}</span>
                )}
              </li>
              <li className={`flex items-center gap-2 ${bodyColor}`}>
                <i className="ri-mail-line text-pink-400 flex-shrink-0"></i>
                {isAdmin ? (
                  <AdminEditField value={data.visitEmail} onSave={(v) => set("visitEmail", v)} inputClassName="text-sm">
                    <span>{data.visitEmail}</span>
                  </AdminEditField>
                ) : (
                  <a href={`mailto:${data.visitEmail}`} className={`transition-colors ${linkHover}`}>{data.visitEmail}</a>
                )}
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className={`border-t pt-6 flex flex-col sm:flex-row items-center justify-between gap-4 ${divider}`}>
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <p className={`text-sm ${bodyColor}`}>
              &copy; {new Date().getFullYear()} Makers Alley Co. All rights reserved.
            </p>
            <span className={`hidden sm:inline text-xs ${bodyColor}`}>·</span>
            <button
              onClick={() => scrollTo("#rules")}
              className={`text-sm cursor-pointer transition-colors ${bodyColor} hover:text-pink-500 whitespace-nowrap`}
            >
              Return Policy
            </button>
          </div>
          {isAdmin ? (
            <AdminEditField value={data.bottomTagline} onSave={(v) => set("bottomTagline", v)} inputClassName="text-sm italic text-center">
              <p className={`text-sm italic ${bodyColor}`}>{data.bottomTagline}</p>
            </AdminEditField>
          ) : (
            <p className={`text-sm italic ${bodyColor}`}>{data.bottomTagline}</p>
          )}
        </div>
      </div>

      {/* Logo editor modal */}
      {showLogoEditor && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40" onClick={() => setShowLogoEditor(false)}>
          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 w-96 space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-white font-bold text-base">Edit Logo</h3>
            <div className="flex gap-2">
              <button
                onClick={() => setLogoDraft({ ...logoDraft, mode: "icon-text" })}
                className={`flex-1 text-xs font-semibold py-2 rounded-full cursor-pointer whitespace-nowrap transition-colors ${
                  logoDraft.mode === "icon-text" ? "bg-pink-500 text-white" : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                }`}
              >
                <i className="ri-font-size mr-1"></i>Icon + Text
              </button>
              <button
                onClick={() => setLogoDraft({ ...logoDraft, mode: "image" })}
                className={`flex-1 text-xs font-semibold py-2 rounded-full cursor-pointer whitespace-nowrap transition-colors ${
                  logoDraft.mode === "image" ? "bg-pink-500 text-white" : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                }`}
              >
                <i className="ri-image-line mr-1"></i>Image Logo
              </button>
            </div>
            {logoDraft.mode === "icon-text" ? (
              <>
                <div>
                  <label className="text-gray-400 text-xs mb-1 block">Icon</label>
                  <div className="flex items-center gap-2">
                    <IconPicker value={logoDraft.icon} onChange={(icon) => setLogoDraft({ ...logoDraft, icon })} />
                    <span className="text-gray-500 text-xs">{logoDraft.icon}</span>
                  </div>
                </div>
                <div>
                  <label className="text-gray-400 text-xs mb-1 block">Logo Text</label>
                  <input type="text" value={logoDraft.text} onChange={(e) => setLogoDraft({ ...logoDraft, text: e.target.value })} className={smallInputCls} />
                </div>
              </>
            ) : (
              <div>
                <label className="text-gray-400 text-xs mb-1 block">Logo Image URL</label>
                <input type="text" value={logoDraft.imageUrl} onChange={(e) => setLogoDraft({ ...logoDraft, imageUrl: e.target.value })} placeholder="https://..." className={smallInputCls} />
                <p className="text-gray-500 text-xs mt-1">Paste a direct image URL. For best results, use a transparent PNG.</p>
                {logoDraft.imageUrl && (
                  <div className="mt-2 p-2 bg-gray-800 rounded-lg">
                    <img src={logoDraft.imageUrl} alt="Logo preview" className="h-8 w-auto object-contain mx-auto" />
                  </div>
                )}
              </div>
            )}
            <div className="flex gap-2 pt-1">
              <button onClick={saveLogo} className="bg-green-500 hover:bg-green-600 text-white text-sm font-bold px-4 py-2 rounded-full cursor-pointer whitespace-nowrap">Save</button>
              <button onClick={() => { setLogoDraft(logoConfig); setShowLogoEditor(false); }} className="text-gray-400 hover:text-gray-200 text-sm cursor-pointer whitespace-nowrap">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit nav link modal */}
      {editingNavId && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40" onClick={() => setEditingNavId(null)}>
          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 w-72 space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-white font-bold text-base">Edit Footer Link</h3>
            <div>
              <label className="text-gray-400 text-xs mb-1 block">Label</label>
              <input type="text" value={navDraft.label || ""} onChange={(e) => setNavDraft({ ...navDraft, label: e.target.value })} className="border border-gray-600 bg-gray-800 text-white rounded-lg px-2 py-1 text-xs focus:outline-none w-full" />
            </div>
            <div>
              <label className="text-gray-400 text-xs mb-1 block">URL / Anchor</label>
              <input type="text" value={navDraft.href || ""} onChange={(e) => setNavDraft({ ...navDraft, href: e.target.value })} className="border border-gray-600 bg-gray-800 text-white rounded-lg px-2 py-1 text-xs focus:outline-none w-full" />
            </div>
            <label className="flex items-center gap-1.5 text-xs text-gray-400 cursor-pointer">
              <input type="checkbox" checked={!!navDraft.external} onChange={(e) => setNavDraft({ ...navDraft, external: e.target.checked })} className="accent-pink-500" />
              External page
            </label>
            <div className="flex gap-2 pt-1">
              <button onClick={saveNavEdit} className="bg-green-500 hover:bg-green-600 text-white text-sm font-bold px-4 py-2 rounded-full cursor-pointer whitespace-nowrap">Save</button>
              <button onClick={() => setEditingNavId(null)} className="text-gray-400 hover:text-gray-200 text-sm cursor-pointer whitespace-nowrap">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </footer>
  );
}
